import { useEffect } from "react";
import Lenis from "lenis";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import TrustBar from "@/components/TrustBar";
import AboutSection from "@/components/AboutSection";
import Services from "@/components/Services";
import Gallery from "@/components/Gallery";
import MapSection from "@/components/MapSection";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import ServiceDetail from "@/pages/ServiceDetail";
import { homeSeo, media } from "@/data/siteContent";

const Home = () => {
  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let lenis;
    let frameId;

    if (!reducedMotion) {
      lenis = new Lenis({
        duration: 1.12,
        smoothWheel: true,
        wheelMultiplier: 0.92,
        touchMultiplier: 1.05,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      });

      const raf = (time) => {
        lenis.raf(time);
        frameId = requestAnimationFrame(raf);
      };
      frameId = requestAnimationFrame(raf);
    }

    const revealItems = Array.from(document.querySelectorAll("main > section, footer"));
    revealItems.forEach((item, index) => {
      item.classList.add("soft-reveal");
      item.classList.add("section-overlap");
      item.style.setProperty("--reveal-delay", `${Math.min(index * 70, 320)}ms`);
    });

    const staggerGroups = Array.from(document.querySelectorAll("[data-stagger-group]"));
    staggerGroups.forEach((group) => {
      const children = Array.from(group.querySelectorAll("[data-stagger-item]"));
      children.forEach((child, index) => {
        child.classList.add("stagger-reveal");
        child.style.setProperty("--stagger-delay", `${index * 100}ms`);
      });
    });

    const imageElements = Array.from(document.querySelectorAll("img"));
    imageElements.forEach((image) => {
      if (!image.hasAttribute("loading")) image.setAttribute("loading", "lazy");
      image.setAttribute("decoding", "async");
      image.classList.add("lazy-image-reveal");
      if (image.complete && image.naturalWidth > 0) {
        image.classList.add("is-loaded");
      } else {
        image.addEventListener("load", () => image.classList.add("is-loaded"), { once: true });
      }
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            entry.target.querySelectorAll(".stagger-reveal").forEach((child) => child.classList.add("is-visible"));
          }
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -8% 0px" },
    );

    revealItems.forEach((item) => observer.observe(item));
    staggerGroups.forEach((group) => observer.observe(group));

    const countElements = Array.from(document.querySelectorAll("[data-count-to]"));
    const countObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.target.dataset.counted === "true") return;
        entry.target.dataset.counted = "true";
        const target = Number(entry.target.dataset.countTo || 0);
        const suffix = entry.target.dataset.countSuffix || "";
        const duration = reducedMotion ? 0 : 1400;
        const start = performance.now();

        const update = (now) => {
          const progress = duration === 0 ? 1 : Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const value = Math.round(target * eased);
          entry.target.textContent = `${value.toLocaleString("tr-TR")}${suffix}`;
          if (progress < 1) requestAnimationFrame(update);
        };
        requestAnimationFrame(update);
      });
    }, { threshold: 0.45 });
    countElements.forEach((item) => countObserver.observe(item));

    const parallaxElements = Array.from(document.querySelectorAll("[data-parallax-speed]"));
    const updateParallax = () => {
      if (reducedMotion) return;
      parallaxElements.forEach((element) => {
        const speed = Number(element.getAttribute("data-parallax-speed") || 0.5);
        const rect = element.parentElement?.getBoundingClientRect();
        if (!rect) return;
        const offset = rect.top * speed * -0.5;
        element.style.transform = `translate3d(0, ${offset}px, 0)`;
      });
    };
    window.addEventListener("scroll", updateParallax, { passive: true });
    updateParallax();

    return () => {
      observer.disconnect();
      countObserver.disconnect();
      window.removeEventListener("scroll", updateParallax);
      if (frameId) cancelAnimationFrame(frameId);
      if (lenis) lenis.destroy();
    };
  }, []);

  return (
    <main data-testid="home-page" className="min-h-screen overflow-hidden bg-[#0A0A0B] text-white">
      <SEO title={homeSeo.title} description={homeSeo.description} path={homeSeo.path} image={media.hero} />
      <Header />
      <Hero />
      <TrustBar />
      <AboutSection />
      <Services />
      <Gallery />
      <MapSection />
      <ContactSection />
      <Footer />
    </main>
  );
};

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/hizmetler/:slug" element={<ServiceDetail />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

export default App;
